import { GetTrendsParams } from "../app/api/trends";
import {
  materialsForBackend,
  stylingDetailsForBackend,
} from "../constants/constants";

const TREND_FEATURE_SESSION_KEYS = [
  ...Object.keys(materialsForBackend),
  ...Object.keys(stylingDetailsForBackend),
];

/**
 * Run Analysis allows one material/styling chip at a time. Session used to merge
 * new params onto the previous run, so leftover `pattern: true` stayed when the
 * user later ran Sleeves. Keep only the most recently written feature flag.
 */
export const sanitizeTrendSessionFeatureFlags = <
  T extends Record<string, unknown>,
>(
  session: T
): T => {
  const next: Record<string, unknown> = { ...session };

  // Color runs store color_analysis_level; restore the chip flag if it was dropped
  // so remount sanitize does not wipe LEVEL1/LEVEL2 context. Only when no other
  // feature chip is present — otherwise keepKey should win and color is cleared.
  const hasColorLevel =
    next.color_analysis_level === "LEVEL1" ||
    next.color_analysis_level === "LEVEL2";
  const otherFeatureSelected = TREND_FEATURE_SESSION_KEYS.some(
    key => key !== "colorAnalysis" && Boolean(next[key])
  );
  if (hasColorLevel && !next.colorAnalysis && !otherFeatureSelected) {
    next.colorAnalysis = true;
  }

  const selectedKeys = Object.keys(next).filter(
    key => TREND_FEATURE_SESSION_KEYS.includes(key) && Boolean(next[key])
  );
  const keepKey = selectedKeys[selectedKeys.length - 1];

  TREND_FEATURE_SESSION_KEYS.forEach(key => {
    if (key !== keepKey) {
      delete next[key];
    }
  });

  if (keepKey !== "colorAnalysis") {
    delete next.selected_color;
    delete next.color_analysis_level;
    delete next.level2Color;
  }

  return next as T;
};

export const trend_analysis_req_ids = {
  fashion_women_ethnic_rtw_most_popular_pattern: "1",
  fashion_women_ethnic_rtw_most_popular_embroidery: "2",
  fashion_women_ethnic_rtw_most_popular_fabric: "3",
  fashion_women_ethnic_rtw_most_popular_shapes: "4",
  fashion_women_ethnic_rtw_most_popular_necklines_and_collars: "5",
  fashion_women_ethnic_rtw_most_popular_placket: "6",
  fashion_women_ethnic_rtw_most_popular_sleeves: "7",
  fashion_women_ethnic_rtw_most_popular_hem_types: "8",
  fashion_women_ethnic_rtw_most_popular_length: "9",
  fashion_women_ethnic_rtw_most_popular_pockets: "10",
  fashion_women_ethnic_rtw_most_popular_details: "11",
  fashion_women_ethnic_rtw_most_popular_waistband: "12",
  fashion_women_western_rtw_most_popular_pattern: "13",
  fashion_women_western_rtw_most_popular_embroidery: "14",
  fashion_women_western_rtw_most_popular_fabric: "15",
  fashion_women_western_rtw_most_popular_shapes: "16",
  fashion_women_western_rtw_most_popular_necklines_and_collars: "17",
  fashion_women_western_rtw_most_popular_placket: "18",
  fashion_women_western_rtw_most_popular_sleeves: "19",
  fashion_women_western_rtw_most_popular_hem_types: "20",
  fashion_women_western_rtw_most_popular_length: "21",
  fashion_women_western_rtw_most_popular_pockets: "22",
  fashion_women_western_rtw_most_popular_details: "23",
  fashion_women_western_rtw_most_popular_waistband: "24",

  fashion_women_ethnic_rtw_new_and_emerging_pattern: "101",
  fashion_women_ethnic_rtw_new_and_emerging_embroidery: "102",
  fashion_women_ethnic_rtw_new_and_emerging_fabric: "103",
  fashion_women_ethnic_rtw_new_and_emerging_shapes: "104",
  fashion_women_ethnic_rtw_new_and_emerging_necklines_and_collars: "105",
  fashion_women_ethnic_rtw_new_and_emerging_placket: "106",
  fashion_women_ethnic_rtw_new_and_emerging_sleeves: "107",
  fashion_women_ethnic_rtw_new_and_emerging_hem_types: "108",
  fashion_women_ethnic_rtw_new_and_emerging_length: "109",
  fashion_women_ethnic_rtw_new_and_emerging_pockets: "110",
  fashion_women_ethnic_rtw_new_and_emerging_details: "111",
  fashion_women_ethnic_rtw_new_and_emerging_waistband: "112",
  fashion_women_western_rtw_pattern: "113",
  fashion_women_western_rtw_embroidery: "114",
  fashion_women_western_rtw_fabric: "115",
  fashion_women_western_rtw_shapes: "116",
  fashion_women_western_rtw_necklines_and_collars: "117",
  fashion_women_western_rtw_placket: "118",
  fashion_women_western_rtw_sleeves: "119",
  fashion_women_western_rtw_hem_types: "120",
  fashion_women_western_rtw_length: "121",
  fashion_women_western_rtw_pockets: "122",
  fashion_women_western_rtw_details: "123",
  fashion_women_western_rtw_waistband: "124",
};

const trendCurveSessionKeys = [
  "curve_window",
  "curve_filter_type",
  "curve_query_type",
] as const;

/** Drop trend-curve-only session fields before calling GET /trends. */
export const stripTrendCurveSessionKeys = <T extends Record<string, unknown>>(
  params: T
): T => {
  const sanitized = { ...params };
  trendCurveSessionKeys.forEach(key => {
    delete sanitized[key];
  });
  return sanitized;
};

export const getReqIdAccordingToSelectedFilter = (
  trendFilters: GetTrendsParams,
  reqIdRef: React.MutableRefObject<string>
) => {
  const {
    shapes,
    sleeves,
    necklines_and_collars,
    hem_types,
    length,
    pockets,
    cuffs,
    details,
    placket,
    waistband,
    fabric,
    pattern,
    embroidery,
    category,
    gender,
    source,
    genre,
    region,
    apparel_type,
    colors,
    start_date,
    end_date,
    brands,
    product,
    age_group,
    colorAnalysis,
    selected_color,
    color_analysis_level,
    level2Color,
    query_type,
  } = trendFilters;

  if (
    colorAnalysis ||
    source ||
    region ||
    brands ||
    product ||
    age_group ||
    colors ||
    start_date ||
    end_date ||
    selected_color ||
    color_analysis_level ||
    level2Color ||
    apparel_type !== "All"
  ) {
    return stripTrendCurveSessionKeys(trendFilters);
  }

  const featureMap: Record<string, boolean | undefined> = {
    pattern,
    embroidery,
    fabric,
    shapes,
    necklines_and_collars,
    placket,
    sleeves,
    hem_types,
    length,
    pockets,
    details,
    waistband,
  };
  /*
  [
    ["pattern", true],
    ["embroidery", false],
    ["fabric", undefined],
    ...
    ]
    this will give value like this
    */
  // Get all selected features
  const selectedFeatures = Object.entries(featureMap).filter(
    ([, isSelected]) => isSelected
  );

  // Proceed only if exactly one feature is selected
  if (selectedFeatures.length !== 1) {
    return stripTrendCurveSessionKeys(trendFilters);
  }

  const [featureKey] = selectedFeatures[0];
  //pattern, embroidery, fabric, shapes, necklines_and_collars, placket, sleeves, hem_types, length, pockets, details, waistband
  // Convert the feature key to lowercase and replace spaces with underscores
  // baseKey exapm : fashion_women_ethnic_rtw_pattern
  const baseKey = `${genre?.toLowerCase()}_${gender?.toLowerCase()}_${category?.toLowerCase().replace(/\s+/g, "_")}_${query_type?.toLowerCase().replace(/\s+/g, "_")}_${featureKey}`;
  if (baseKey in trend_analysis_req_ids) {
    const req_id =
      trend_analysis_req_ids[baseKey as keyof typeof trend_analysis_req_ids];
    reqIdRef.current = req_id;
    trendFilters.req_id = req_id;
    return stripTrendCurveSessionKeys({
      ...trendFilters,
      req_id,
    });
  } else {
    console.warn("No matching req_id found for", baseKey);
    return stripTrendCurveSessionKeys(trendFilters);
  }
};

export const formatTrendHeaderChips = (value: string): string => {
  if (!value) return value;
  return value.includes("+") ? value.split("+")[0].trim() : value;
};
